"use client";
import React, { type JSX, useEffect, useState, useTransition } from "react";
import classNames from "classnames";
import { notFound } from "next/navigation";
import { WarningCircle } from "iconoir-react";
import { InfoBox } from "@cappelendamm/design-system/react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { ContentData } from "@/cms";
import { SubscriptionSection } from "@/checkout/Sections/SubscriptionSection/SubscriptionSection";
import { ChildInformationSection } from "@/checkout/Sections/ChildInformationSection/ChildInformationSection";
import { SubscriptionSummarySection } from "@/checkout/Sections/SubscriptionSummarySection/SubscriptionSummarySection";
import { CustomerSection } from "@/checkout/Sections/CustomerSection/CustomerSection";
import { SelectedSubscription } from "@/checkout/Sections/SubscriptionSection/types";
import { CheckoutContentQueryResult } from "@/cms/sanity.types";
import { PaymentSection } from "@/checkout/Sections/PaymentSection/PaymentSection";
import { getCheckoutPayloadFromSku } from "@/checkout/checkoutStorage";
import { fillFormFromCheckoutPayload } from "@/checkout/subscription/utils";
import { useSubscriptionCheckoutForm } from "@/checkout/subscription/useSubscriptionCheckoutForm";
import { CheckoutForm } from "@/checkout/types";
import { CheckoutSubmit } from "@/checkout/Sections/CheckoutSubmit";
import { CheckoutAutofill } from "@/checkout/CheckoutAutofill";
import { TrackCheckoutSubscribe } from "./TrackCheckoutSubscribe";
import { useTrackCheckoutSubmit } from "@/checkout/tracking";
import { datadogLogs } from "@datadog/browser-logs";
import { submitCheckoutSubscribe } from "./_actions";
import { BookClubCheckoutSummary } from "./BookClubCheckoutSummary";

export type CheckoutProps = ContentData<CheckoutContentQueryResult> & {
  selectedSubscription: SelectedSubscription;
  termsSection?: React.ReactNode;
};

function getErrorMessage(
  status: Extract<
    Awaited<ReturnType<typeof submitCheckoutSubscribe>>,
    { success: false }
  >["status"],
  { isBookClub }: { isBookClub: boolean },
) {
  switch (status) {
    case "CUSTOMER_SUSPENDED":
    case "CUSTOMER_SUSPENSION_ERROR":
      return "Faktura er ikke tilgjengelig for øyeblikket. Velg en annen betalingsmåte, eller kontakt kundeservice.";
    case "CUSTOMER_ALREADY_SUBSCRIBING":
      return isBookClub
        ? "Du er allerede medlem av denne klubben!"
        : "Du har allerede et aktivt abonnement!";
    default:
      return "En feil oppstod. Vennligst prøv igjen, en annen betalingsmetode, eller kontakt kundeservice.";
  }
}

export const Checkout = ({
  selectedSubscription,
  termsSection,
  data,
}: CheckoutProps & JSX.IntrinsicElements["div"]) => {
  const { newsletterTerms } = data;
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [isPending, startTransition] = useTransition();
  const { subscription } = selectedSubscription;

  const form = useSubscriptionCheckoutForm(selectedSubscription);
  const { trackSubmitted, trackValidationFailed, trackSubmitFailed } =
    useTrackCheckoutSubmit("subscription");

  useEffect(() => {
    if (subscription.sku) {
      const checkoutPayload = getCheckoutPayloadFromSku(subscription.sku);
      if (checkoutPayload) {
        fillFormFromCheckoutPayload(form, checkoutPayload);
      }
    }
  }, [subscription.sku]);

  if (!subscription) {
    return notFound();
  }

  const handleSubmit: SubmitHandler<CheckoutForm> = (data) => {
    trackSubmitted(data.payment.type);
    startTransition(async () => {
      setSubmitErrorMessage("");
      const result = await submitCheckoutSubscribe({
        data,
        newsLetterData: {
          newsletterTopicId: newsletterTerms?.newsletterTopicId ?? "",
          newsletterPurposeId: newsletterTerms?.newsletterPurposeId ?? "",
        },
      });
      if (result?.success === false) {
        trackSubmitFailed(result.status, data.payment.type);
        datadogLogs.logger.error(
          "checkout_error",
          {
            flow: "subscription",
            stage: "submit",
            paymentType: data.payment.type,
            subscriptionSku: subscription.sku,
            errorMessage: result.status,
          },
          result.error instanceof Error ? result.error : undefined,
        );

        setSubmitErrorMessage(
          getErrorMessage(result.status, {
            isBookClub: !!subscription.isBookClub,
          }),
        );
      }
    });
  };

  return (
    <FormProvider {...form}>
      <CheckoutAutofill
        startTransition={startTransition}
        checkoutMeta={{ type: "subscription", sku: subscription.sku }}
      />
      <div className="min-h-screen overflow-x-scroll lg:overflow-x-visible">
        <main className="[&_legend]:font-system [&_h2]:font-system app-grid px-4 md:mx-body md:p-0 [&_legend]:cvr-title-md [&_legend]:font-semibold">
          <h1 className="sr-only">Utsjekk & Betaling</h1>
          <form
            className={classNames(
              "col-span-12 space-y-6 pb-8 sm:col-span-4 sm:col-start-2 md:col-span-6 md:col-start-4",
              {
                "lg:col-span-10 lg:col-start-2 lg:grid lg:grid-cols-2 lg:items-start lg:space-y-0 lg:gap-x-8 lg:gap-y-4":
                  subscription.isBookClub,
              },
              {
                "animate-pulse": isPending,
              },
            )}
            onSubmit={form.handleSubmit(handleSubmit, trackValidationFailed)}
          >
            <div
              className={classNames({
                "lg:col-span-2": subscription.isBookClub,
              })}
            >
              <SubscriptionSection
                subscription={subscription}
                isPending={isPending}
                hideGiftSelection={subscription.isBookClub}
              />
            </div>

            <div
              className={classNames("space-y-6", {
                "lg:col-span-1": subscription.isBookClub,
              })}
            >
              {subscription.isBookClub ? (
                <div className="lg:hidden">
                  <BookClubCheckoutSummary
                    subscription={subscription}
                    subscriptionImage={selectedSubscription.subscriptionImage}
                  />
                </div>
              ) : null}

              <ChildInformationSection
                isPending={isPending}
                isChildClubSubscription={subscription.isChildClub}
              />

              <CustomerSection isPending={isPending} />

              <PaymentSection
                isPending={isPending}
                immediateReservation={false}
              />

              {termsSection}

              {submitErrorMessage ? (
                <InfoBox className="mt-2 flex items-center gap-4" role="alert">
                  <WarningCircle className="min-w-8" width={38} height={38} />
                  <p>{submitErrorMessage}</p>
                </InfoBox>
              ) : null}

              <CheckoutSubmit isPending={isPending} />

              {!subscription.isBookClub ? (
                <SubscriptionSummarySection subscription={subscription} />
              ) : null}
            </div>

            {subscription.isBookClub ? (
              <aside className="hidden h-full lg:col-span-1 lg:block lg:self-start">
                <div className="lg:sticky lg:top-6">
                  <BookClubCheckoutSummary
                    subscription={subscription}
                    subscriptionImage={selectedSubscription.subscriptionImage}
                  />
                </div>
              </aside>
            ) : null}
          </form>
        </main>
      </div>
      <TrackCheckoutSubscribe />
    </FormProvider>
  );
};

export default Checkout;
